import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, Search, Split, Trash2 } from 'lucide-react';
import {
  SPLITTER_KIND_LABELS,
  type OpticalSplitter,
  type Splitter,
  type SplitterKind,
  type Topology,
} from '@simpleacs/shared';
import {
  Button,
  Card,
  EmptyState,
  ErrorNotice,
  PageHeader,
  Pill,
  Spinner,
  StatCard,
  TBody,
  THead,
  TRow,
  Table,
} from '../components/ui';
import { api } from '../lib/api';
import { useSession } from '../lib/session';
import {
  ConfirmDeleteDialog,
  OpticalSplitterDialog,
  upstreamSummary,
} from './OpticalSplitterDialog';

/**
 * The registry of physical splitter devices, flat on purpose — the same
 * objects the topology tree shows inside housings, without the topology UI.
 * Each row says where the device sits (ODF / enclosure / FAT) and where its
 * light comes from, including the splices the tree cannot select.
 */
export function SplittersPage() {
  const { can } = useSession();
  const queryClient = useQueryClient();
  const [dialog, setDialog] = useState<{ existing: OpticalSplitter | null } | null>(null);
  const [deleting, setDeleting] = useState<OpticalSplitter | null>(null);
  const [filter, setFilter] = useState('');

  const topologyQuery = useQuery({
    queryKey: ['topology'],
    queryFn: () => api.get<Topology>('/topology'),
  });
  const topology = topologyQuery.data;
  const devices = useMemo(() => topology?.opticalSplitters ?? [], [topology]);
  const housings = useMemo(
    () => new Map((topology?.splitters ?? []).map((s) => [s.id, s] as const)),
    [topology],
  );

  const shown = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const rows = q
      ? devices.filter(
          (d) =>
            d.name.toLowerCase().includes(q) ||
            (d.housingId && housings.get(d.housingId)?.name.toLowerCase().includes(q)) ||
            (d.upstreamComment ?? '').toLowerCase().includes(q),
        )
      : devices;
    return [...rows].sort((a, b) => a.name.localeCompare(b.name));
  }, [devices, housings, filter]);

  const housed = devices.filter((d) => d.housingId).length;

  return (
    <>
      <PageHeader
        title="Splitters"
        description="Every physical 1:N device — where it sits, and where its light comes from."
        actions={
          can('topology.manage') ? (
            <Button
              variant="primary"
              icon={<Plus className="size-4" />}
              onClick={() => setDialog({ existing: null })}
            >
              New splitter
            </Button>
          ) : null
        }
      />

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard label="Splitters" value={devices.length} icon={<Split className="size-4" />} />
        <StatCard label="Housed" value={housed} />
        <StatCard label="Not housed yet" value={devices.length - housed} tone={devices.length - housed > 0 ? 'warn' : 'default'} />
        <StatCard
          label="Spliced to another splitter"
          value={devices.filter((d) => d.upstreamKind === 'splitter').length}
        />
      </div>

      {topologyQuery.isLoading ? (
        <div className="flex justify-center p-16">
          <Spinner className="size-6" />
        </div>
      ) : topologyQuery.error ? (
        <div className="mt-5">
          <ErrorNotice error={topologyQuery.error} />
        </div>
      ) : (
        <div className="mt-5">
          <Card
            title="Registry"
            action={
              <span className="relative">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-faint" />
                <input
                  className="input h-8 w-56 pl-8 text-xs"
                  placeholder="Filter by name, housing, comment…"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                />
              </span>
            }
          >
            {shown.length === 0 ? (
              <EmptyState
                icon={<Split className="size-8" />}
                title={devices.length === 0 ? 'No splitters registered' : 'Nothing matches'}
                description={
                  devices.length === 0
                    ? 'Register each physical device here or from the topology tree — the two are the same registry.'
                    : 'No splitter, housing or comment matches that filter.'
                }
              />
            ) : (
              <Table>
                <THead>
                  <th className="th">Name</th>
                  <th className="th">Ratio</th>
                  <th className="th">Housed in</th>
                  <th className="th">Upstream</th>
                  {can('topology.manage') && <th className="th w-20" />}
                </THead>
                <TBody>
                  {shown.map((device) => {
                    const housing = device.housingId ? housings.get(device.housingId) : null;
                    const upstream = upstreamSummary(device, devices);
                    return (
                      <TRow key={device.id}>
                        <td className="td font-medium text-ink">{device.name}</td>
                        <td className="td font-mono text-xs">1:{device.outputs}</td>
                        <td className="td">
                          {housing ? (
                            <span className="flex items-center gap-2">
                              {housing.name}
                              <Pill tone="info">
                                {SPLITTER_KIND_LABELS[housing.kind as SplitterKind]}
                              </Pill>
                            </span>
                          ) : (
                            <Pill tone="warn">not housed</Pill>
                          )}
                        </td>
                        <td className="td max-w-[26rem]">
                          {upstream ? (
                            <span className="block truncate text-muted" title={upstream}>
                              {upstream}
                            </span>
                          ) : (
                            <span className="text-faint">—</span>
                          )}
                        </td>
                        {can('topology.manage') && (
                          <td className="td">
                            <span className="flex justify-end gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                title="Edit splitter"
                                icon={<Pencil className="size-3.5" />}
                                onClick={() => setDialog({ existing: device })}
                              />
                              <Button
                                size="sm"
                                variant="ghost"
                                title="Delete splitter"
                                icon={<Trash2 className="size-3.5" />}
                                onClick={() => setDeleting(device)}
                              />
                            </span>
                          </td>
                        )}
                      </TRow>
                    );
                  })}
                </TBody>
              </Table>
            )}
          </Card>
        </div>
      )}

      {dialog && topology && (
        <OpticalSplitterDialog
          existing={dialog.existing}
          housings={sortedHousings(topology.splitters)}
          devices={devices}
          onClose={() => setDialog(null)}
        />
      )}
      {deleting && (
        <ConfirmDeleteDialog
          title={`Delete ${deleting.name}?`}
          body={
            <p>
              The registry entry is removed — nothing on the chassis changes. A splitter other
              devices are spliced to is refused by name.
            </p>
          }
          onConfirm={async () => {
            await api.delete(`/optical-splitters/${encodeURIComponent(deleting.id)}`);
            await queryClient.invalidateQueries({ queryKey: ['topology'] });
          }}
          onClose={() => setDeleting(null)}
        />
      )}
    </>
  );
}

/** Housings sorted ODF → enclosure → FAT then by name, so the select reads like the light path. */
export function sortedHousings(splitters: Splitter[]): Splitter[] {
  const rank: Record<string, number> = { odf: 0, enclosure: 1, fat: 2 };
  return [...splitters].sort(
    (a, b) => (rank[a.kind] ?? 3) - (rank[b.kind] ?? 3) || a.name.localeCompare(b.name),
  );
}
